import { kpLiveApi } from '@/services/kursi-play-v2/kpLiveApiClient';
import { loadPracticeCenterData } from '@/features/unified-learning/runtime/practiceCenterService';
import type { KpQualityTier, KpScenarioRuntime } from '@/types/kursi-play-v2';

export type ReverseQueueItem = {
  lessonId: string;
  lessonTitle: string;
  scenario: KpScenarioRuntime;
  source: 'mistake' | 'weakness';
};

export type ReverseSessionSummary = {
  total: number;
  answered: number;
  masteryRate: number;
  improvedCount: number;
  riskyCount: number;
  weakCount: number;
  xpEarned: number;
};

const MASTERED_TIERS: KpQualityTier[] = ['strong', 'acceptable'];

export async function buildReverseLearningQueue(
  slug: string,
  userId: string,
  targetQuestions = 8,
): Promise<{ queue: ReverseQueueItem[]; weakLessonIds: string[]; mistakeScenarioIds: string[] }> {
  const center = await loadPracticeCenterData(slug, userId);
  const unlockedLessons = center.lessons.filter((lesson) => lesson.status !== 'locked').map((lesson) => lesson.id);
  const weakLessonIds = center.weakLessonIds.filter((id) => unlockedLessons.includes(id));

  if (weakLessonIds.length === 0) {
    return { queue: [], weakLessonIds: [], mistakeScenarioIds: [] };
  }

  const runtimes = await Promise.all(weakLessonIds.map((lessonId) => kpLiveApi.getLessonRuntime(lessonId, slug, userId)));
  const mistakeSet = new Set(center.recentMistakeScenarioIds);

  const prioritized: ReverseQueueItem[] = [];
  const fallback: ReverseQueueItem[] = [];

  for (const rt of runtimes) {
    for (const scenario of rt.scenarios) {
      const item: ReverseQueueItem = {
        lessonId: rt.lesson.id,
        lessonTitle: rt.lesson.title,
        scenario,
        source: mistakeSet.has(scenario.id) ? 'mistake' : 'weakness',
      };

      if (item.source === 'mistake') prioritized.push(item);
      else fallback.push(item);
    }
  }

  const queue = [...prioritized, ...fallback].slice(0, Math.max(1, targetQuestions));

  return {
    queue,
    weakLessonIds,
    mistakeScenarioIds: center.recentMistakeScenarioIds,
  };
}

export function summarizeReverseSession(input: {
  answered: number;
  xpEarned: number;
  total: number;
  qualityTiers: KpQualityTier[];
}): ReverseSessionSummary {
  const { answered, xpEarned, total, qualityTiers } = input;
  const improvedCount = qualityTiers.filter((tier) => MASTERED_TIERS.includes(tier)).length;
  const riskyCount = qualityTiers.filter((tier) => tier === 'risky').length;
  const weakCount = qualityTiers.filter((tier) => tier === 'weak').length;
  const masteryRate = answered > 0 ? Math.round((improvedCount / answered) * 100) : 0;

  return {
    total,
    answered,
    masteryRate,
    improvedCount,
    riskyCount,
    weakCount,
    xpEarned,
  };
}
